import { useEffect, useState } from 'react';
import { Alert, Skeleton } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { API } from 'apis';
import { getViaAuth, responseValidator } from 'apis/api';
import { DateFilterValues } from 'core/domain/overview/overview';
import { TrxUsdtReportData } from 'core/domain/overview/trx-usdt-report';
import { jFormat, jFormatEndOfDay } from 'core/utils/jFormat';
import './trx-usdt-report.scss';

interface Props {
    range: DateFilterValues;
    refreshKey: number;
}

const formatNumber = (value: number) =>
    new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 8 }).format(value);

export const TrxUsdtReport = ({ range, refreshKey }: Props) => {
    const [data, setData] = useState<TrxUsdtReportData>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);
        setData(undefined);

        const load = async () => {
            try {
                const response: any = await getViaAuth<TrxUsdtReportData>(API.dashboard.getTrxUsdtReport, {
                    startDate: jFormat(range.startDate) || undefined,
                    endDate: jFormatEndOfDay(range.endDate) || undefined,
                });
                if (!active) return;
                if (responseValidator(response.status) && response.isSuccess !== false && response.value) {
                    setData(response.value);
                } else {
                    setError(true);
                }
            } catch {
                if (active) setError(true);
            } finally {
                if (active) setLoading(false);
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [range.startDate, range.endDate, refreshKey, retry]);

    const groups = [
        {
            symbol: 'TRX',
            title: 'ترون (TRX)',
            accent: 'red',
            count: data?.countTronWithdrawals,
            amount: data?.sumTronWithdrawals,
            fee: data?.sumTronWithdrawalsFee,
        },
        {
            symbol: 'USDT',
            title: 'تتر (USDT)',
            accent: 'green',
            count: data?.countUSDTWithdrawals,
            amount: data?.sumUSDTWithdrawals,
            fee: data?.sumUSDTWithdrawalsFee,
        },
    ];

    return (
        <section className="overview-section trx-usdt-report" aria-label="گزارش TRX/USDT" aria-busy={loading}>
            <div className="section-head">
                <h3>گزارش TRX/USDT</h3>
                <p>برداشت و کارمزد ترون و تتر · برداشت‌های موفق در بازه انتخابی</p>
                <button
                    type="button"
                    className="report-refresh"
                    disabled={loading}
                    onClick={() => setRetry((value) => value + 1)}
                >
                    <ReloadOutlined spin={loading} />
                    به‌روزرسانی گزارش
                </button>
            </div>
            {error ? (
                <Alert
                    type="error"
                    showIcon
                    message="دریافت گزارش برداشت و کارمزد ناموفق بود."
                    action={
                        <button
                            type="button"
                            className="report-refresh"
                            onClick={() => setRetry((value) => value + 1)}
                        >
                            تلاش مجدد
                        </button>
                    }
                />
            ) : (
                <div className="report-cards">
                    {groups.map((group) => (
                        <article className={`report-card accent-${group.accent}`} key={group.symbol}>
                            <header>
                                <img src={`/assets/images/coins/${group.symbol.toLowerCase()}.svg`} alt="" />
                                <div>
                                    <h4>{group.title}</h4>
                                    <span>برداشت و کارمزد</span>
                                </div>
                            </header>
                            <dl>
                                {[
                                    {
                                        label: 'تعداد برداشت',
                                        value: group.count,
                                        unit: 'عدد',
                                    },
                                    {
                                        label: 'مجموع مبلغ برداشت',
                                        value: group.amount,
                                        unit: group.symbol,
                                    },
                                    {
                                        label: 'کارمزد دریافتی',
                                        value: group.fee,
                                        unit: group.symbol,
                                    },
                                ].map((metric) => (
                                    <div className="report-metric" key={metric.label}>
                                        <dt>{metric.label}</dt>
                                        <dd>
                                            {loading ? (
                                                <Skeleton.Input active size="small" style={{ width: 100 }} />
                                            ) : (
                                                <>
                                                    <b>{formatNumber(metric.value ?? 0)}</b>
                                                    <span>{metric.unit}</span>
                                                </>
                                            )}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
};
